import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { crmApi } from "@/services/phase1Api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Building2,
  Check,
  ChevronDown,
  CheckCircle,
  ClipboardCheck,
  CreditCard,
  FileText,
  FolderKanban,
  ListChecks,
  Loader2,
  Lock,
  LogOut,
  Shield,
  UserPlus,
  ArrowRight,
  Download,
  LineChart,
  Gauge,
  Rocket,
  Star,
  MessageCircle,
  FileCheck2,
  Monitor,
  FolderOpen,
  BookOpen,
  ListTree,
  History as HistoryIcon,
  Beaker,
  ClipboardList,
  Calendar as CalendarIcon,
  LayoutDashboard,
} from "lucide-react";
import AssessmentReport from "./app/AssessmentReport";
import SOWView from "./app/SOWView";
import CaporilliDiagnostic from "./app/CaporilliDiagnostic";
import RoadmapV2 from "./app/portal/RoadmapV2";
import ProjectTrackerV2 from "./app/portal/ProjectTrackerV2";

import FileAttachments from "@/components/FileAttachments";
import WorkflowSelection from "./app/portal/WorkflowSelection";
import { PlaybookOverview, PlaybookDetail } from "./app/portal/Playbook";
import CEOReportV2 from "./app/admin/CEOReportV2";
import CustomerCostDashboard from "./app/portal/CustomerCostDashboard";
import ClarityWordmark from "@/components/brand/ClarityWordmark";
import PaymentConfirmedInline from "./portal/PaymentConfirmed";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* ──────────────────────────────────────────────────────────────────
   Progress Tracker — Visual journey indicator
   ────────────────────────────────────────────────────────────────── */
const JOURNEY_STEPS = [
  { key: "assessment", label: "Assessment", icon: ClipboardCheck },
  { key: "scope", label: "Scope", icon: ListChecks },
  { key: "sow", label: "Scope of Services", icon: FileText },
  { key: "payment", label: "Payment", icon: CreditCard },
  { key: "plan", label: "Plan", icon: FileText },
  { key: "project", label: "Workspace", icon: FolderKanban },
];

const ProgressTracker = ({ hasSOW, sowApproved, implementationPaid, hasProject, retainerPaid, bestResult, planApproved, launchPlanApproved }) => {
  const getStepStatus = (key) => {
    switch (key) {
      case "assessment": return bestResult ? "complete" : "active";
      case "scope": return planApproved ? "complete" : bestResult ? "active" : "upcoming";
      case "sow": return sowApproved ? "complete" : hasSOW ? "active" : planApproved ? "active" : "upcoming";
      case "payment": return implementationPaid ? "complete" : sowApproved ? "active" : "upcoming";
      case "plan": return launchPlanApproved ? "complete" : implementationPaid ? "active" : "upcoming";
      case "project": return (hasProject && launchPlanApproved) ? "active" : "upcoming";
      default: return "upcoming";
    }
  };

  return (
    <div className="mb-8" data-testid="progress-tracker">
      <div className="rounded-xl border border-[#e5e7eb] bg-white px-6 py-5">
        <div className="flex items-center justify-between gap-0">
          {JOURNEY_STEPS.map((step, idx) => {
            const status = getStepStatus(step.key);
            const isLast = idx === JOURNEY_STEPS.length - 1;
            const stepNumber = idx + 1;

            return (
              <React.Fragment key={step.key}>
                <div className="flex flex-col items-center gap-2 min-w-0 flex-1" data-testid={`progress-step-${step.key}`}>
                  <div
                    className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 text-[14px] font-bold ${
                      status === "complete"
                        ? "bg-[#10b981] text-white"
                        : status === "active"
                          ? "bg-[#1a56db] text-white"
                          : "bg-[#f3f4f6] text-[#9ca3af]"
                    }`}
                  >
                    {status === "complete" ? <Check className="h-4 w-4" strokeWidth={3} /> : stepNumber}
                  </div>
                  <span className={`text-[13px] font-medium text-center leading-tight ${
                    status === "complete"
                      ? "text-[#10b981]"
                      : status === "active"
                        ? "text-[#1a56db]"
                        : "text-[#9ca3af]"
                  }`}>
                    {step.label}
                  </span>
                </div>
                {!isLast && (
                  <div className={`h-[2px] flex-1 max-w-[80px] -mt-6 ${
                    getStepStatus(JOURNEY_STEPS[idx + 1].key) === "complete" || status === "complete"
                      ? "bg-[#10b981]"
                      : status === "active"
                        ? "bg-[#1a56db]/30"
                        : "bg-[#e5e7eb]"
                  }`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────────
   Portal Header — Clean Executive Clarity: brand + company + user
   ────────────────────────────────────────────────────────────────── */
const PortalHeader = ({ tenantName, tenantId, userName, onInvite, onSignOut }) => {
  const [exporting, setExporting] = React.useState(false);
  const initials = (userName || "U")
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const handleExportOpenClaw = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/export/tenants/${tenantId}/openclaw`);
      if (!res.ok) throw new Error("Export failed");
      const data = await res.json();
      const name = (data.customer?.company_name || "tenant").replace(/[^a-zA-Z0-9]/g, "_");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `openclaw_handoff_${name}_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("OpenClaw handoff exported");
    } catch (err) {
      toast.error("Export failed: " + err.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <header className="sticky top-0 z-50 border-b border-[#e5e7eb] bg-white">
      <div className="px-5 sm:px-5">
        <div className="flex items-center justify-between h-[60px] gap-6">
          {/* LEFT — Brand + tenant name (aligned to sidebar text — 20px left padding) */}
          <div className="flex items-center gap-4 min-w-0">
            <div className="flex items-center shrink-0">
              <ClarityWordmark height={35} />
            </div>
            <div className="h-7 w-px bg-[#e5e7eb] shrink-0" />
            <h1
              className="text-[16px] font-bold tracking-tight text-[#111827] truncate"
              style={{ letterSpacing: "-0.01em" }}
              data-testid="portal-header-tenant-name"
            >
              {tenantName}
            </h1>
          </div>

          {/* RIGHT — Export + user menu */}
          <div className="flex items-center gap-3 shrink-0 pr-4">
            <button
              onClick={handleExportOpenClaw}
              disabled={exporting}
              className="hidden sm:inline-flex h-9 items-center gap-1.5 rounded-lg border border-[#e5e7eb] bg-white px-3 text-[12px] font-medium text-[#4b5563] transition-colors hover:bg-[#f9fafb] disabled:opacity-50"
              data-testid="export-openclaw-btn"
            >
              {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              <span>Export</span>
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="inline-flex h-10 items-center gap-2.5 rounded-full border border-[#e5e7eb] bg-white px-2 pr-3 transition-colors hover:bg-[#f9fafb]"
                >
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#e8eefb] text-[11px] font-semibold text-[#1a56db]">
                    {initials}
                  </div>
                  <span className="text-[13px] font-medium text-[#111827]">{userName}</span>
                  <ChevronDown className="h-3.5 w-3.5 text-[#9ca3af]" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-[220px] border border-[#e5e7eb] bg-white">
                <DropdownMenuLabel className="text-[#9ca3af] text-[11px] font-semibold tracking-wider uppercase">
                  {tenantName}
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-[#e5e7eb]" />
                <DropdownMenuItem className="cursor-pointer text-[#111827] focus:bg-[#f9fafb]" onClick={onInvite}>
                  <UserPlus className="mr-2 h-4 w-4" />
                  Invite User
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-[#e5e7eb]" />
                <DropdownMenuItem className="cursor-pointer text-[#111827] focus:bg-[#f9fafb]" onClick={onSignOut}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
};

/* ──────────────────────────────────────────────────────────────────
   Assets — customer-uploaded supporting files for deeper AI analysis
   ────────────────────────────────────────────────────────────────── */
const AssetsView = ({ tenantId, session }) => {
  const effectiveSession = session || { tenant: { id: tenantId || "tenant_1" }, user: { id: "client", role: "client" } };
  return (
    <div className="mt-2 max-w-4xl" data-testid="assets-view">
      <div className="mb-6">
        <h1 className="text-[28px] font-bold tracking-[-0.02em] text-[#111827]">Artifacts</h1>
        <p className="mt-1 text-[14px] text-[#6b7280] leading-relaxed">
          Upload supporting documents, screenshots, exports, and reference materials here.
          Your 589 Labs consultant will review these for deeper AI analysis alongside your assessment answers.
        </p>
      </div>

      <div className="rounded-2xl border border-[#e5e7eb] bg-white p-6">
        <FileAttachments
          session={effectiveSession}
          context="tenant_asset"
          contextId={tenantId}
          label="Your Uploaded Artifacts"
          emptyHint="No files uploaded yet. Drag one in or click below."
          showLabelInput
          dataTestidPrefix="asset"
        />
      </div>

      <div className="mt-6 rounded-xl border border-[#fde68a] bg-[#fffbeb] p-4">
        <div className="text-[11px] font-bold tracking-[0.10em] uppercase text-[#92400e] mb-1">
          Tips for AI Analysis
        </div>
        <ul className="text-[13px] text-[#78350f] leading-relaxed list-disc pl-5 space-y-1">
          <li>Org charts, workflow diagrams, and process documentation help us map AI opportunities.</li>
          <li>Sample outputs (CSV exports, reports) let us assess data quality and volume.</li>
          <li>Screenshots of dashboards or tools help us understand your current tech stack.</li>
          <li>All uploads are private to your tenant and only visible to your assigned 589 Labs team.</li>
        </ul>
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────────
   Portal Sidebar — Your Journey (grouped menu)
   ────────────────────────────────────────────────────────────────── */
const SIDEBAR_GROUPS = [
  {
    key: "main",
    label: null, // Dashboard stands alone — no group label
    items: [
      { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, tab: "dashboard", gate: "ai_cost_dashboard" },
    ],
  },
  {
    key: "assessments",
    label: "Your Progress",
    items: [
      { key: "readiness", label: "Preliminary Assessment", icon: Gauge, tab: "report" },
      { key: "opportunities", label: "In-Depth Assessment", icon: Star, tab: "report", gate: "detailed_report" },
      { key: "workflows", label: "AI Workflow Plan", icon: ClipboardList, tab: "scope", gate: "workflow_plan" },
    ],
  },
  {
    key: "implementation",
    label: "Implementation",
    items: [
      { key: "sow", label: "Scope of Services", icon: FileText, tab: "sow", gate: "sow" },
      { key: "launch_plan", label: "Roadmap", icon: CalendarIcon, tab: "launch_plan", gate: "launch_plan" },
      { key: "playbooks", label: "Playbooks", icon: Rocket, tab: "playbooks", gate: "playbooks", isNew: true },
      { key: "workspace", label: "Project Tracker", icon: Monitor, tab: "workspace", gate: "workspace" },
    ],
  },
  {
    key: "resources",
    label: "Resources",
    items: [
      { key: "diagnostic", label: "Diagnostic", icon: BookOpen, tab: "cdu", educationOnly: true },
      { key: "assets", label: "Artifacts", icon: FolderOpen, tab: "assets" },
    ],
  },
];

const PortalDashboardPlaceholder = ({ tenantId, tenantName, onJump, projectStarted, lockMap }) => {
  return (
    <div className="max-w-3xl mx-auto py-8" data-testid="portal-dashboard-placeholder">
      <div
        className="rounded-2xl border bg-white p-10"
        style={{ borderColor: "rgba(0,0,0,0.08)" }}
      >
        <div
          className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-bold tracking-[0.10em] uppercase mb-4"
          style={{ background: "rgba(59,79,228,0.10)", color: "#3B4FE4" }}
        >
          Coming Soon
        </div>
        <h2 className="text-[28px] font-bold tracking-tight" style={{ color: "#0F1117", letterSpacing: "-0.02em" }}>
          Welcome back{tenantName ? `, ${tenantName}` : ""}
        </h2>
        <p className="mt-2 text-[15px]" style={{ color: "#5C6070", maxWidth: 560 }}>
          Your executive snapshot — assessments, workflows, implementation status, and project milestones in one view — is launching next. In the meantime, jump into the section you need below.
        </p>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { key: "opportunities", tab: "report", label: "Detailed Report", sub: "Review your AI Readiness analysis", lockKey: null },
            { key: "workflows", tab: "scope", label: "AI Workflow Plan", sub: "See your selected workflows", lockKey: "workflow_plan" },
            { key: "sow", tab: "sow", label: "Scope of Services", sub: "Approve and unlock implementation", lockKey: "sow" },
            { key: "launch_plan", tab: "launch_plan", label: "Roadmap", sub: "Your 30/60/90 plan", lockKey: "launch_plan" },
          ].map((it) => {
            // D-04 (May-2026): disable cards whose target section is still locked
            // (i.e. project hasn't started OR prerequisites not met). Cards stay
            // visible but become non-interactive with a clear "available later"
            // tooltip — they auto-enable when prerequisites are reached.
            const isLocked = it.lockKey ? !(lockMap?.[it.lockKey]?.unlocked) : false;
            return (
              <button
                key={it.key}
                type="button"
                onClick={() => !isLocked && onJump(it)}
                disabled={isLocked}
                title={isLocked ? (lockMap?.[it.lockKey]?.message || "Available once your project begins") : undefined}
                className="text-left rounded-[12px] border bg-white px-4 py-3 transition-colors hover:border-[#3B4FE4] disabled:hover:border-[rgba(0,0,0,0.08)]"
                style={{
                  borderColor: "rgba(0,0,0,0.08)",
                  opacity: isLocked ? 0.38 : 1,
                  cursor: isLocked ? "not-allowed" : "pointer",
                }}
                data-testid={`portal-dashboard-jump-${it.key}`}
              >
                <div className="text-[14px] font-semibold" style={{ color: "#0F1117" }}>{it.label} {!isLocked ? "→" : ""}</div>
                <div className="text-[12px] mt-0.5" style={{ color: "#5C6070" }}>
                  {isLocked ? (lockMap?.[it.lockKey]?.message || "Available once your project begins") : it.sub}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const PortalSidebar = ({ activeTab, activeItemKey, onNavigate, menuAccess, tenantIndustry, tenantName }) => {
  const isEducation = (tenantIndustry || "").toLowerCase() === "education";
  const groups = SIDEBAR_GROUPS
    .map((g) => ({
      ...g,
      items: g.items.filter((it) => !it.educationOnly || isEducation),
    }))
    .filter((g) => g.items.length > 0);
  return (
    <aside
      className="hidden lg:flex flex-col w-[228px] shrink-0 min-h-[calc(100vh-60px)] sticky top-[60px] self-start text-white"
      style={{ backgroundColor: "#3B4FE4" }}
      data-testid="portal-sidebar"
    >
      {/* Org pill removed per user feedback — tenant name still visible in top bar */}

      <nav className="flex-1 pb-8 overflow-y-auto pt-3">
        {groups.map((group, gi) => {
          const isStandalone = !group.label;
          return (
            <div key={group.key}>
              {gi > 0 && (
                <div
                  aria-hidden
                  style={{ height: 1, background: "rgba(255,255,255,0.14)", margin: "8px 18px" }}
                />
              )}
              {!isStandalone && (
                <div
                  className="px-[18px] mb-1 mt-2 text-[10px] font-bold tracking-[0.12em] uppercase"
                  style={{ color: "rgba(255,255,255,0.55)" }}
                  data-testid={`sidebar-group-${group.key}`}
                >
                  {group.label}
                </div>
              )}
              <div>
                {group.items.map((item) => {
                  const isActive = activeItemKey === item.key;
                  const gateMeta = item.gate ? menuAccess?.[item.gate] : null;
                  const locked = item.gate ? !(gateMeta?.unlocked ?? true) : false;
                  const Icon = item.icon;

                  let bg = "transparent";
                  let color = "rgba(255,255,255,0.92)";
                  let borderLeft = "3px solid transparent";
                  let fontWeight = 500;
                  if (locked) {
                    color = "rgba(255,255,255,0.28)";
                  } else if (isActive) {
                    bg = "rgba(255,255,255,0.14)";
                    color = "#fff";
                    borderLeft = "3px solid rgba(255,255,255,0.90)";
                    fontWeight = 600;
                  }

                  return (
                    <button
                      key={item.key}
                      type="button"
                      disabled={locked}
                      onClick={() => !locked && onNavigate(item)}
                      title={locked ? (gateMeta?.message || "Locked") : ""}
                      className={`w-full flex items-center gap-2.5 pl-[18px] pr-3 py-[9px] text-[13px] text-left transition-colors ${
                        locked ? "cursor-not-allowed" : "hover:bg-[rgba(255,255,255,0.08)]"
                      }`}
                      style={{ backgroundColor: bg, color, borderLeft, fontWeight }}
                      data-testid={`sidebar-item-${item.key}`}
                    >
                      {locked ? (
                        <Lock className="h-[15px] w-[15px] shrink-0" />
                      ) : (
                        <Icon className="h-[15px] w-[15px] shrink-0" />
                      )}
                      <span className="truncate flex-1">{item.label}</span>
                      {item.isNew && !locked && (
                        <span
                          className="px-[7px] py-[2px] text-[9px] font-extrabold uppercase tracking-wider rounded-full"
                          style={{ backgroundColor: "rgba(255,255,255,0.22)", color: "#fff" }}
                        >
                          New
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>
    </aside>
  );
};

/* ──────────────────────────────────────────────────────────────────
   Retainer Options (dynamic from Super Admin pricing)
   ────────────────────────────────────────────────────────────────── */
const RetainerOptions = ({ tenantId, onPurchased }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/pricing/public`)
      .then(r => r.json())
      .then(d => setPlans(d.retainer_plans || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handlePurchase = async (plan) => {
    setPurchasing(plan.id);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/tenants/${tenantId}/project/retainer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan_id: plan.id, plan_name: plan.plan_name || plan.name, price: plan.monthly_price || plan.price }),
      });
      if (resp.ok) {
        toast.success(`${plan.plan_name || plan.name} retainer activated!`);
        onPurchased();
      } else {
        toast.error("Failed to process retainer");
      }
    } catch {
      toast.error("Failed to process retainer");
    } finally {
      setPurchasing(null);
    }
  };

  if (loading) return null;
  if (!plans.length) return null;

  return (
    <div className="mb-6" data-testid="retainer-options">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl bg-[#e8eefb] flex items-center justify-center shrink-0">
          <Shield className="h-5 w-5 text-[#1a56db]" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-foreground">Retainer Packages Available</h3>
          <p className="text-sm text-foreground/50 mt-0.5">Select the plan that best fits your needs. Upgrade or downgrade anytime.</p>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        {plans.filter(p => p.is_active).map((plan) => (
          <div key={plan.id} className="rounded-[12px] border border-[#e5e7eb] bg-white p-5 flex flex-col" data-testid={`retainer-plan-${plan.id}`}>
            <div className="flex-1">
              <Badge className="mb-2 text-[11px] font-semibold bg-[#f3f4f6] text-[#6b7280] hover:bg-[#f3f4f6]">{plan.plan_name || plan.name}</Badge>
              <div className="text-[26px] font-bold text-[#111827] tracking-tight">
                ${(plan.monthly_price || plan.price)?.toLocaleString()}<span className="text-[13px] font-normal text-[#9ca3af]">/mo</span>
              </div>
              <p className="mt-2 text-[13px] text-[#4b5563] leading-[1.55]">{plan.summary_description || plan.description}</p>
              {plan.features?.length > 0 && (
                <ul className="mt-3 space-y-1.5">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-[13px] text-[#374151]">
                      <Check className="h-4 w-4 text-[#059669] shrink-0 mt-0.5" strokeWidth={3} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <Button
              onClick={() => handlePurchase(plan)}
              disabled={purchasing === plan.id}
              className="mt-4 w-full h-10 rounded-[8px] bg-[#1a56db] text-white text-[13px] font-semibold hover:bg-[#143faa]"
              data-testid={`purchase-retainer-${plan.id}`}
            >
              {purchasing === plan.id ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CreditCard className="h-4 w-4 mr-1.5" />}
              Select Plan
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────────
   Post-SOW Approval Payment Container
   ────────────────────────────────────────────────────────────────── */
const PostApprovalPayment = ({ tenantId, onPaid }) => {
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [thankYou, setThankYou] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/pricing/public`)
      .then(r => r.json())
      .then(d => setPricing(d.pricing || {}))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handlePay = async () => {
    if (paying || loading) return; // Guard — prerequisites must be ready
    setPaying(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/tenants/${tenantId}/project/record-payment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (resp.ok) {
        setThankYou(true);
        // Immediately refresh state so the portal advances
        onPaid();
        // Navigate via React Router so the portal shell + sidebar stay rendered.
        // Intentionally do NOT reset `paying` — component unmounts on popstate.
        window.history.replaceState({}, "", `/portal/${tenantId}/payment/scope-confirmed`);
        window.dispatchEvent(new PopStateEvent("popstate"));
      } else {
        toast.error("Payment processing failed");
        setPaying(false);
      }
    } catch {
      toast.error("Payment processing failed");
      setPaying(false);
    }
  };

  if (loading) return null;

  if (thankYou) {
    return (
      <div className="mt-8 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-7 sm:p-8" data-testid="payment-thank-you">
        <div className="flex flex-col items-center text-center space-y-3">
          <CheckCircle className="h-12 w-12 text-emerald-500" />
          <h3 className="text-lg font-semibold text-foreground">Thank You for Your Payment!</h3>
          <p className="text-sm text-foreground/60 max-w-md leading-relaxed">
            Your implementation project payment has been recorded successfully. Your Project Workspace is now activating — you'll have full access momentarily.
          </p>
          <p className="text-xs text-foreground/40">A confirmation email will be sent to your registered email address.</p>
        </div>
      </div>
    );
  }

  const implPrice = pricing?.implementation?.amount || 9995;

  // Item 5 (May-2026): Black "Your Next Step" payment box — matches the
  // CEOReportV2 next-step-cta visual language. Rendered at the bottom of the
  // Scope of Services page only. The duplicate top-of-page card has been removed.
  return (
    <div
      className="mt-10 mb-4 rounded-2xl"
      style={{
        background: "#0F1117",
        color: "#fff",
        padding: "26px 28px",
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gap: 22,
        alignItems: "center",
        fontFamily: "'DM Sans', system-ui, sans-serif",
      }}
      data-testid="post-approval-payment"
    >
      <div>
        <div
          style={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.55)",
            marginBottom: 8,
          }}
        >
          Your Next Step
        </div>
        <div
          style={{
            fontSize: 24,
            fontWeight: 700,
            letterSpacing: "-0.02em",
            lineHeight: 1.2,
            marginBottom: 10,
          }}
        >
          Activate Your Implementation Project
        </div>
        <div style={{ fontSize: 14, lineHeight: 1.6, color: "rgba(255,255,255,0.75)", maxWidth: 640 }}>
          Your Scope of Services is approved. Complete the ${implPrice.toLocaleString()} Implementation
          Project payment to activate your Project Tracker, kickoff call, and your dedicated 589 Labs
          delivery team — the fastest path from scope to live deployment.
        </div>
      </div>
      <button
        type="button"
        onClick={handlePay}
        disabled={paying || loading}
        className="inline-flex items-center justify-center rounded-xl px-6 py-4 text-[15px] font-bold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        style={{
          background: "#3B4FE4",
          color: "#fff",
          minWidth: 240,
          boxShadow: "0 6px 18px rgba(59,79,228,0.32)",
        }}
        data-testid="pay-implementation-btn"
      >
        {(paying || loading) ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
        {paying ? "Processing…" : "Activate Implementation →"}
      </button>
      <style>{`
        @media (max-width: 720px) {
          [data-testid="post-approval-payment"] {
            grid-template-columns: 1fr !important;
          }
          [data-testid="post-approval-payment"] button {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────────
   Portal Login Screen
   ────────────────────────────────────────────────────────────────── */
const PortalLogin = ({ tenantId, tenantName, onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [contactEmail, setContactEmail] = useState("");

  // Fetch the expected contact email for the tenant to show as hint
  useEffect(() => {
    if (!tenantId) return;
    fetch(`${BACKEND_URL}/api/provisioned-tenants/${tenantId}`)
      .then(r => r.ok ? r.json() : null)
      .then(t => { if (t?.contact_email) setContactEmail(t.contact_email); })
      .catch(() => {});
  }, [tenantId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const resp = await fetch(`${BACKEND_URL}/api/tenant-auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenant_id: tenantId || "", username, password }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || "Login failed");
      }
      const data = await resp.json();
      onLogin(data);
    } catch (e) {
      setError(e.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f9fafb] flex items-center justify-center p-4">
      <Card className="w-full max-w-sm border border-[#e5e7eb] bg-white">
        <CardContent className="p-8">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-6">
              <ClarityWordmark height={72} />
            </div>
            <Building2 className="h-10 w-10 text-[#1a56db] mx-auto mb-3" />
            <h1 className="text-[20px] font-bold text-[#111827]">{tenantName || "Client Portal"}</h1>
            <p className="text-[13px] text-[#9ca3af] mt-1">Sign in to view your AI Readiness Report</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-[11px] font-semibold tracking-[0.12em] text-[#111827] uppercase mb-1.5 block">Email</label>
              <Input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={contactEmail || "your@email.com"}
                className="h-11 rounded-lg border-[#e5e7eb] bg-white text-[#111827] placeholder:text-[#9ca3af]"
                data-testid="portal-username"
                autoFocus
              />
              {contactEmail && !username && (
                <button type="button" onClick={() => setUsername(contactEmail)} className="text-[10px] text-[#1a56db] hover:underline mt-1">
                  Use {contactEmail}
                </button>
              )}
            </div>
            <div>
              <label className="text-[11px] font-semibold tracking-[0.12em] text-[#111827] uppercase mb-1.5 block">Password</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="h-11 rounded-lg border-[#e5e7eb] bg-white text-[#111827] placeholder:text-[#9ca3af]"
                data-testid="portal-password"
              />
            </div>
            {error && (
              <div className="text-[12px] text-[#b91c1c] bg-[#fef2f2] border border-[#fecaca] rounded-lg p-2.5">
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading || !username || !password}
              className="w-full h-11 rounded-lg bg-[#1a56db] text-white hover:bg-[#143faa] font-semibold shadow-none"
              data-testid="portal-login-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />}
              Sign In
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────────
   In-Depth Assessment CTA — Shown after FREE assessment in light portal
   ────────────────────────────────────────────────────────────────── */
const DetailedAssessmentCTA = ({ tenantId, onPurchased }) => {
  const [pricing, setPricing] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [purchased, setPurchased] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/pricing/public`)
      .then(r => r.json())
      .then(d => setPricing(d.pricing || {}))
      .catch(() => {})
      .finally(() => setPricingLoading(false));
  }, []);

  const assessmentPrice = pricing?.assessment?.amount || 4999;

  const handlePurchase = async () => {
    if (purchasing || pricingLoading) return; // Guard — never fire before prerequisites settle
    setPurchasing(true);
    setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/tenants/${tenantId}/purchase-assessment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Payment failed");
      setPurchased(true);
      if (onPurchased) onPurchased();
      // Navigate via React Router so the portal shell + sidebar stay rendered.
      window.history.replaceState({}, "", `/portal/${tenantId}/payment/assessment-confirmed`);
      window.dispatchEvent(new PopStateEvent("popstate"));
    } catch (e) {
      setError(e.message);
      setPurchasing(false);
    }
    // Note: do NOT reset setPurchasing on success — component unmounts via popstate
  };

  if (purchased) {
    return (
      <Card className="mb-6 border-emerald-500/20 bg-emerald-500/5 p-6" data-testid="detailed-assessment-purchased">
        <div className="flex flex-col items-center text-center space-y-2">
          <CheckCircle className="h-10 w-10 text-emerald-500" />
          <h3 className="text-base font-semibold text-foreground">Payment Confirmed</h3>
          <p className="text-sm text-foreground/50 max-w-md">Your In-Depth Assessment has been purchased. Your 589 Labs consultant will reach out to schedule the assessment.</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="mb-6 border-[#bfdbfe] bg-[#eff6ff] p-5 sm:p-6" data-testid="detailed-assessment-cta">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-white flex items-center justify-center shrink-0 mt-0.5 border border-[#bfdbfe]">
            <ClipboardCheck className="h-5 w-5 text-[#1a56db]" />
          </div>
          <div>
            <h3 className="text-base font-bold text-[#111827]">Unlock Your Full AI Readiness Report</h3>
            <p className="text-sm text-[#4b5563] mt-1 max-w-lg leading-relaxed">
              Your free assessment is complete. Upgrade to the In-Depth Assessment for a comprehensive analysis
              across all pillars, cross-question insights, and a prioritized implementation roadmap.
            </p>
            {error && <p className="text-xs text-[#b91c1c] mt-1">{error}</p>}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <Button
            onClick={handlePurchase}
            disabled={purchasing || pricingLoading}
            className="h-10 rounded-lg bg-[#1a56db] text-white text-sm font-semibold hover:bg-[#143faa] px-6"
            data-testid="purchase-detailed-assessment-btn"
          >
            {(purchasing || pricingLoading) ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <ArrowRight className="h-4 w-4 mr-1.5" />}
            {pricingLoading ? "Loading…" : "Purchase In-Depth Assessment"}
          </Button>
          <span className="text-[10px] text-[#9ca3af]">Pricing details available at checkout</span>
        </div>
      </div>
    </Card>
  );
};

/* ──────────────────────────────────────────────────────────────────
   Portal Area — main entry point
   ────────────────────────────────────────────────────────────────── */
export default function PortalArea() {
  const { tenantId: urlTenantId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [tenantId, setTenantId] = useState(urlTenantId || "");
  const [portalSession, setPortalSession] = useState(() => {
    try {
      const saved = sessionStorage.getItem(`portal_session_${urlTenantId || "global"}`);
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [portal, setPortal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tenantName, setTenantName] = useState("");
  const [activeTab, setActiveTab] = useState("report");
  const [activeItemKey, setActiveItemKey] = useState("opportunities");
  const [playbookId, setPlaybookId] = useState(null);
  const [paymentVariant, setPaymentVariant] = useState(null); // "assessment" | "scope" | null

  // Deep-link: /portal/:tenantId/payment/{assessment-confirmed|scope-confirmed}
  useEffect(() => {
    const m = location.pathname.match(/\/portal\/[^/]+\/payment\/(assessment|scope)-confirmed/);
    if (m) {
      setPaymentVariant(m[1]);
      setActiveTab("payment_confirmed");
    }
  }, [location.pathname]);

  // Deep-link: /portal/:tenantId/playbooks/:playbookId  →  open Playbook detail
  useEffect(() => {
    const m = location.pathname.match(/\/portal\/[^/]+\/playbooks\/([^/?#]+)/);
    if (m) {
      setActiveTab("playbooks");
      setPlaybookId(m[1]);
      setActiveItemKey("playbooks");
    }
  }, [location.pathname]);

  // Keep sidebar highlight in sync when tab is changed programmatically
  useEffect(() => {
    const DEFAULT_FOR_TAB = {
      report: "opportunities",
      cdu: "diagnostic",
      scope: "workflows",
      sow: "sow",
      launch_plan: "launch_plan",
      workspace: "workspace",
      assets: "assets",
      playbooks: "playbooks",
      dashboard: "dashboard",
    };
    const expected = DEFAULT_FOR_TAB[activeTab];
    if (expected) {
      const belongsToTab = [
        ...(activeTab === "dashboard" ? ["dashboard"] : []),
        ...(activeTab === "report" ? ["readiness", "opportunities"] : []),
        ...(activeTab === "cdu" ? ["diagnostic", "overview", "history", "whatif", "action"] : []),
        ...(activeTab === "scope" ? ["workflows"] : []),
        ...(activeTab === "sow" ? ["sow"] : []),
        ...(activeTab === "launch_plan" ? ["launch_plan"] : []),
        ...(activeTab === "workspace" ? ["workspace"] : []),
        ...(activeTab === "assets" ? ["assets"] : []),
        ...(activeTab === "playbooks" ? ["playbooks"] : []),
      ];
      if (!belongsToTab.includes(activeItemKey)) {
        setActiveItemKey(expected);
      }
    }
  }, [activeTab, activeItemKey]);

  // ── Centralized lifecycle state (source of truth) ──
  const [lifecycleState, setLifecycleState] = useState(null);

  // Derived booleans from lifecycle state (for backward compat with existing components)
  const hasProject = lifecycleState?.workspace_state !== "locked";
  const hasSOW = lifecycleState?.sow_state !== "none" && lifecycleState?.sow_state !== undefined;
  const sowApproved = lifecycleState?.sow_state === "approved";
  const implementationPaid = lifecycleState?.project_payment_state === "paid";
  const planApproved = lifecycleState?.implementation_plan_state === "approved" || lifecycleState?.implementation_plan_state === "ready";
  const launchPlanApproved = lifecycleState?.implementation_plan_state === "approved" && lifecycleState?.workspace_state === "active";
  const retainerPaid = ["RETAINER_ACTIVE", "RETAINER_AT_RISK"].includes(lifecycleState?.lifecycle_state);
  const projectStatus = lifecycleState?.workspace_state === "complete" ? "complete" : lifecycleState?.workspace_state === "active" ? "active" : null;
  const menuAccess = lifecycleState?.menu_access || {};
  const contactName = portal?.contact?.full_name || "";

  // FIX-09: reactive sidebar lock state based on customer progress.
  const detailedAssessmentComplete =
    ["detailed_delivered", "detailed_in_progress", "detailed_complete", "scope_confirmed", "project_active"].includes(lifecycleState?.assessment_state || "")
    || !!(portal?.results || []).some((r) => r.assessment_type === "detailed" && r.overall_readiness_score != null);
  const workflowsConfirmed = !!(
    (portal?.prospect?.confirmed_workflows || []).length
    || (portal?.prospect?.pre_selected_workflows || []).length
  );
  const scopeConfirmed = sowApproved || planApproved || workflowsConfirmed && (menuAccess?.sow?.unlocked === true);
  const richMenuAccess = {
    ...menuAccess,
    detailed_report: {
      unlocked: detailedAssessmentComplete,
      message: detailedAssessmentComplete ? "" : "Available once your In-Depth Assessment is complete",
    },
    workflow_plan: {
      // Item 4 (May-2026): The AI Workflow Plan unlocks the moment the customer
      // has clicked "Choose Your Top 3 Workflows" from the report. We treat any
      // recommended_workflows or assessment_state >= detailed_complete as a
      // sufficient signal — no other prerequisite. The previous detailed-only
      // gate was occasionally off because the assessment_state field lags the
      // actual UI completion by a tick.
      unlocked: detailedAssessmentComplete
        || workflowsConfirmed
        || !!(portal?.recommended_workflows || portal?.prospect?.recommended_workflows || []).length,
      message: (detailedAssessmentComplete || workflowsConfirmed) ? "" : "Available once your In-Depth Assessment is complete",
    },
    sow: {
      unlocked: (menuAccess?.sow?.unlocked ?? workflowsConfirmed),
      message: (menuAccess?.sow?.unlocked ?? workflowsConfirmed) ? "" : "Available once you confirm your AI workflows",
    },
    launch_plan: {
      unlocked: (menuAccess?.launch_plan?.unlocked ?? sowApproved),
      message: (menuAccess?.launch_plan?.unlocked ?? sowApproved) ? "" : "Available once your Scope of Services is approved",
    },
    workspace: {
      // FIX-07 (May-2026): Project Tracker gates on roadmap_approved (= planApproved) only.
      unlocked: (menuAccess?.workspace?.unlocked ?? planApproved),
      message: (menuAccess?.workspace?.unlocked ?? planApproved) ? "" : "Available once your Roadmap is approved",
    },
    playbooks: {
      // FIX-07 (May-2026): Playbooks gates on roadmap_approved (= planApproved) only.
      unlocked: planApproved,
      message: planApproved ? "" : "Available once your Roadmap is approved",
    },
    // Item 13 (May-2026): AI Cost & Results dashboard activates ONLY after the
    // scope/implementation payment is recorded. We map this to the existing
    // `project_payment_state === "paid"` lifecycle marker (= implementationPaid).
    ai_cost_dashboard: {
      unlocked: implementationPaid,
      message: implementationPaid ? "" : "Activates once your scope payment is complete",
    },
  };

  // Fetch tenant name for login page
  useEffect(() => {
    const fetchTenantName = async () => {
      if (!tenantId) return;
      try {
        const resp = await fetch(`${BACKEND_URL}/api/provisioned-tenants/${tenantId}`);
        if (resp.ok) {
          const data = await resp.json();
          setTenantName(data.name || "Client Portal");
        }
      } catch { /* ignore */ }
    };
    fetchTenantName();
  }, [tenantId]);

  // ── Poll lifecycle state (replaces checkProject + checkSOW) ──
  const refreshState = useCallback(async () => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/tenants/${tenantId}/state`);
      if (resp.ok) {
        const state = await resp.json();
        setLifecycleState(state);
      }
    } catch { /* ignore */ }
  }, [tenantId]);

  // Load portal data after login
  const loadPortal = useCallback(async (sess) => {
    setLoading(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/tenants/${tenantId}/portal`, {
        headers: {
          "x-tenant-id": sess.tenant?.id || "tenant_1",
          "x-user-id": sess.user?.id || "public",
          "x-user-role": sess.user?.role || "tenant_admin",
        },
      });
      if (resp.ok) {
        const data = await resp.json();
        setPortal(data);
      }
    } catch {
      toast.error("Failed to load portal data");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  const handleLogin = (sessionData) => {
    const resolvedTenantId = sessionData.tenant?.id;
    if (resolvedTenantId && (!urlTenantId || urlTenantId !== resolvedTenantId)) {
      setTenantId(resolvedTenantId);
      navigate(`/portal/${resolvedTenantId}`, { replace: true });
    }
    // Persist session so page refresh doesn't force logout
    try { sessionStorage.setItem(`portal_session_${resolvedTenantId || urlTenantId || "global"}`, JSON.stringify(sessionData)); } catch {}
    setPortalSession(sessionData);
    loadPortal(sessionData);
    refreshState();
  };

  // Poll lifecycle state every 5 seconds for reactive updates (no logout/login needed)
  useEffect(() => {
    if (portalSession && tenantId) {
      refreshState();
      const interval = setInterval(refreshState, 5000);
      return () => clearInterval(interval);
    }
  }, [portalSession, tenantId, refreshState]);

  // Auto-reload portal data when session is restored or state changes significantly
  const prevLifecycleRef = React.useRef(null);
  useEffect(() => {
    if (portalSession && tenantId && !portal) {
      loadPortal(portalSession);
    }
  }, [portalSession, tenantId, portal, loadPortal]);

  useEffect(() => {
    if (!portalSession || !lifecycleState) return;
    const prev = prevLifecycleRef.current;
    const curr = lifecycleState.lifecycle_state;
    if (prev && prev !== curr) {
      // Lifecycle changed — refresh portal data
      loadPortal(portalSession);
    }
    prevLifecycleRef.current = curr;
  }, [lifecycleState, portalSession, loadPortal]);

  const handleInvite = async () => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/tenants/${tenantId}/invite`, {
        method: "POST",
        headers: {
          "x-tenant-id": "tenant_1",
          "x-user-id": portalSession?.user?.id || "public",
          "x-user-role": "admin",
        },
      });
      if (resp.ok) {
        const data = await resp.json();
        await navigator.clipboard.writeText(data.invite_link);
        toast.success("Invite link copied to clipboard", { description: data.invite_link });
      }
    } catch {
      toast.error("Failed to generate invite link");
    }
  };

  const handleSignOut = () => {
    try { sessionStorage.removeItem(`portal_session_${tenantId || "global"}`); } catch {}
    setPortalSession(null);
    setPortal(null);
  };

  // Show login if not authenticated
  if (!portalSession) {
    return <PortalLogin tenantId={tenantId} tenantName={tenantName} onLogin={handleLogin} />;
  }

  if (loading || !portal) {
    return (
      <div className="min-h-screen bg-[#f9fafb]">
        <PortalHeader
          tenantName={portalSession.tenant?.name || tenantName}
          tenantId={tenantId}
          userName={portalSession.user?.full_name || "Admin"}
          onInvite={handleInvite}
          onSignOut={handleSignOut}
        />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-[#9ca3af]" />
        </div>
      </div>
    );
  }

  // Find the best result (prefer detailed)
  const detailedResult = portal.results?.find(
    (r) => (r.session_info?.assessment_type || r.assessment_type) === "detailed"
  );
  const bestResult = detailedResult || portal.results?.[0];

  // Build a fake session object for AssessmentReport compatibility
  const fakeSession = {
    tenant: { id: portalSession.tenant?.id || "tenant_1" },
    user: { id: portalSession.user?.id || "public", role: "tenant_admin" },
  };

  const handleSidebarNavigate = (item) => {
    if (item.tab && item.tab !== activeTab) {
      setActiveTab(item.tab);
    }
    setActiveItemKey(item.key);
    if (item.tab === "playbooks" && playbookId) {
      setPlaybookId(null);
      navigate(`/portal/${tenantId}`, { replace: true });
    }
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
  };

  return (
    <div className="min-h-screen bg-[#f9fafb]" data-testid="portal-area">
      <PortalHeader
        tenantName={portalSession.tenant?.name || tenantName}
        tenantId={tenantId}
        userName={portalSession.user?.full_name || "Admin"}
        onInvite={handleInvite}
        onSignOut={handleSignOut}
      />

      <div className="flex">
        <PortalSidebar
          activeTab={activeTab}
          activeItemKey={activeItemKey}
          onNavigate={handleSidebarNavigate}
          menuAccess={richMenuAccess}
          tenantIndustry={portal?.tenant?.industry || portalSession?.tenant?.industry}
          tenantName={portalSession.tenant?.name || tenantName}
        />

        <main className={`flex-1 min-w-0 ${(activeTab === "launch_plan" || activeTab === "workspace") ? "" : "px-6 py-8 sm:px-10"}`}>
          {/* FIX-04b/06 (May-2026): Roadmap & Project Tracker fill full width and own
              their stepper. Other tabs keep the legacy 1200px content cap. */}
          <div className={(activeTab === "launch_plan" || activeTab === "workspace") ? "w-full" : "mx-auto max-w-[1200px]"}>
          {/* Progress Tracker — hidden on CDU (Diagnostic) and on the new
              Roadmap/Project Tracker pages which already render their own
              6-step stepper (FIX-06). */}
          {activeTab !== "cdu" && activeTab !== "launch_plan" && activeTab !== "workspace" && (
            <ProgressTracker
              hasSOW={hasSOW}
              sowApproved={sowApproved}
              implementationPaid={implementationPaid}
              hasProject={hasProject}
              retainerPaid={retainerPaid}
              bestResult={bestResult}
              planApproved={planApproved}
              launchPlanApproved={launchPlanApproved}
            />
          )}

          {/* In-Depth Assessment CTA — hidden in initialMode of the Preliminary
              report view to remove duplicate purchase containers (D-02 May-2026).
              Also hidden on the payment_confirmed page (Item 3, May-2026): once
              the customer has paid, the "Your Assessment Is Underway" screen
              must NOT show another Purchase prompt. Item 4 (May-2026): also
              hide once the lifecycle has moved beyond free_completed (i.e.
              customer has paid OR completed the in-depth flow). */}
          {activeTab !== "cdu"
            && activeTab !== "report"
            && activeTab !== "dashboard"
            && activeTab !== "payment_confirmed"
            && lifecycleState?.assessment_state === "free_completed"
            && !lifecycleState?.detailed_paid_at
            && !lifecycleState?.has_in_depth_results
            && (
            <DetailedAssessmentCTA tenantId={tenantId} onPurchased={refreshState} />
          )}

          {/* Retainer Options moved into the Project Tracker tab only —
              they no longer appear on Dashboard / SoW / Roadmap / etc.
              See the ProjectTrackerV2 render branch below. */}

          {/* Post-SOW-Approval Payment Container */}
          {/* PostApprovalPayment moved into SoW tab content (Item 5, May-2026)
              so the black "Activate Implementation" CTA appears at the bottom
              of the Scope of Services page only — and never on other tabs. */}

          {/* Tab Content */}
          {activeTab === "payment_confirmed" ? (
            <PaymentConfirmedInline
              variant={paymentVariant || "assessment"}
              onBackToPortal={() => {
                setPaymentVariant(null);
                setActiveTab("dashboard");
                setActiveItemKey("dashboard");
                navigate(`/portal/${tenantId}`, { replace: true });
              }}
            />
          ) : activeTab === "dashboard" ? (
            (() => {
              // Item 13 (May-2026): Once scope/implementation payment is
              // recorded (= implementationPaid), the Dashboard tab renders the
              // new AI Cost & Results dashboard. Until then, we keep the
              // existing fallback chain (Preliminary CEO Report → placeholder).
              if (implementationPaid) {
                const dashTenant = {
                  organization_name: portal?.account?.name || portal?.tenant?.name || tenantName,
                  name: tenantName,
                  prospect: portal?.prospect || {},
                  confirmed_workflows: portal?.prospect?.confirmed_workflows || [],
                };
                return <CustomerCostDashboard tenant={dashTenant} />;
              }
              // Item 2 (May-2026): on first login the Dashboard tab now renders
              // the Preliminary Assessment Report (CEOReportV2 in initialMode)
              // instead of the "Coming Soon" placeholder. Falls back to the
              // placeholder only when no preliminary result exists yet.
              const initialResult = portal?.results?.find(
                (r) => (r.session_info?.assessment_type || r.assessment_type) === "initial"
              );
              if (initialResult) {
                const prospectInfo = {
                  company_name: portal?.account?.name || portal?.tenant?.name || tenantName,
                  contact_name: portal?.contact?.full_name || "",
                  contact_email: portal?.contact?.email || "",
                };
                return (
                  <CEOReportV2
                    result={initialResult}
                    prospectInfo={prospectInfo}
                    tenantId={tenantId}
                    portalMode={true}
                    initialMode={true}
                    sowApproved={sowApproved}
                    onUpgradeCTA={() => {
                      setActiveTab("scope");
                      setActiveItemKey("workflows");
                      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
                    }}
                  />
                );
              }
              return (
                <PortalDashboardPlaceholder
                  tenantId={tenantId}
                  tenantName={tenantName}
                  onJump={(item) => { setActiveTab(item.tab); setActiveItemKey(item.key); }}
                  projectStarted={hasProject}
                  lockMap={richMenuAccess}
                />
              );
            })()
          ) : activeTab === "cdu" ? (
            <div className="mt-2">
              <CaporilliDiagnostic
                tenantId={tenantId}
                externalTab={activeItemKey}
                onTabChange={(t) => setActiveItemKey(t)}
                hideTabs={true}
              />
            </div>
          ) : activeTab === "report" ? (
            <>
              {(() => {
                if (!bestResult) {
                  return (
                    <Card className="border border-[#e5e7eb] bg-white">
                      <CardContent className="p-8 text-center">
                        <Building2 className="h-10 w-10 text-[#d1d5db] mx-auto mb-4" />
                        <h3 className="text-[16px] font-semibold text-[#111827] mb-2">No Assessment Reports Yet</h3>
                        <p className="text-[14px] text-[#9ca3af] max-w-md mx-auto">
                          Your AI Readiness Assessment is still in progress. Reports will appear here once completed.
                        </p>
                      </CardContent>
                    </Card>
                  );
                }
                // When the customer is viewing the Detailed Report and we have a detailed result,
                // render the contiguous CEO Report v2 (customer-tuned).
                const showDetailedV2 = activeItemKey === "opportunities" && detailedResult;
                if (showDetailedV2) {
                  const prospectInfo = {
                    company_name: portal?.account?.name || portal?.tenant?.name || tenantName,
                    contact_name: portal?.contact?.full_name || "",
                    contact_email: portal?.contact?.email || "",
                  };
                  return (
                    <CEOReportV2
                      result={detailedResult}
                      prospectInfo={prospectInfo}
                      tenantId={tenantId}
                      portalMode={true}
                      sowApproved={sowApproved}
                      onChooseWorkflows={() => { setActiveTab("scope"); setActiveItemKey("workflows"); setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50); }}
                    />
                  );
                }
                // Preliminary Assessment — render CEO Report v2 in initialMode (FIX-07)
                const initialResult = portal.results?.find(
                  (r) => (r.session_info?.assessment_type || r.assessment_type) === "initial"
                );
                const showInitialV2 = activeItemKey === "readiness" && initialResult;
                if (showInitialV2) {
                  const prospectInfo = {
                    company_name: portal?.account?.name || portal?.tenant?.name || tenantName,
                    contact_name: portal?.contact?.full_name || "",
                    contact_email: portal?.contact?.email || "",
                  };
                  return (
                    <CEOReportV2
                      result={initialResult}
                      prospectInfo={prospectInfo}
                      tenantId={tenantId}
                      initialMode={true}
                      portalMode={true}
                      onUpgradeCTA={async () => {
                        try {
                          const r = await fetch(`${BACKEND_URL}/api/tenants/${tenantId}/purchase-assessment`, {
                            method: "POST", headers: { "Content-Type": "application/json" },
                          });
                          if (r.ok) {
                            window.history.replaceState({}, "", `/portal/${tenantId}/payment/assessment-confirmed`);
                            window.dispatchEvent(new PopStateEvent("popstate"));
                          }
                        } catch {
                          // no-op
                        }
                      }}
                    />
                  );
                }
                return (
                  <AssessmentReport
                    session={fakeSession}
                    resultId={bestResult}
                    portalMode={true}
                    externalActiveSection={activeTab === "report" ? activeItemKey : null}
                    onActiveSectionChange={(s) => setActiveItemKey(s)}
                  />
                );
              })()}
            </>
          ) : activeTab === "sow" ? (
            <div className="mt-2">
              <SOWView tenantId={tenantId} />
              {/* Item 5 — black payment CTA at the bottom of the SoW page */}
              {sowApproved && !implementationPaid && (
                <PostApprovalPayment tenantId={tenantId} onPaid={refreshState} />
              )}
            </div>
          ) : activeTab === "scope" ? (
            <WorkflowSelection
              tenantId={tenantId}
              prospectId={portal?.prospect?.id || portal?.prospect_id}
              recommendedWorkflows={
                /* Always use the FULL recommended list (typically 5) so the customer can
                   freely swap their top 3. confirmed_workflows is used for IDs only. */
                portal?.recommended_workflows
                || lifecycleState?.recommended_workflows
                || portal?.prospect?.recommended_workflows
                || portal?.prospect?.pre_selected_workflows
                || []
              }
              preSelectedIds={
                (portal?.prospect?.confirmed_workflows || portal?.prospect?.pre_selected_workflows || [])
                  .map((w) => w.id || w.workflow_id)
                  .filter(Boolean)
              }
              /* FIX-04c (May-2026): once scope is confirmed (SOW generated or
                 plan/launch already in motion), this view becomes read-only. */
              isLocked={hasSOW || sowApproved || planApproved}
              onConfirmed={() => { refreshState(); setActiveTab("sow"); }}
            />
          ) : activeTab === "playbooks" ? (
            playbookId ? (
              <PlaybookDetail
                tenantId={tenantId}
                playbookId={playbookId}
                onBack={() => { setPlaybookId(null); navigate(`/portal/${tenantId}`, { replace: true }); }}
              />
            ) : (
              <PlaybookOverview
                tenantId={tenantId}
                prospectId={portal?.prospect?.id || portal?.prospect_id}
                onOpenPlaybook={(id) => {
                  setPlaybookId(id);
                  navigate(`/portal/${tenantId}/playbooks/${id}`, { replace: false });
                }}
              />
            )
          ) : activeTab === "launch_plan" ? (
            <div className="mt-2">
              <RoadmapV2
                tenantId={tenantId}
                onApproved={() => { refreshState(); setActiveTab("workspace"); }}
              />
            </div>
          ) : activeTab === "assets" ? (
            <AssetsView tenantId={tenantId} session={portalSession} />
          ) : (
            <>
              {/* CAIO Retainer payment options — show ONLY on the Project
                  Tracker, and only once the project is marked complete. */}
              {hasProject && !retainerPaid && projectStatus === "complete" && (
                <RetainerOptions tenantId={tenantId} onPurchased={refreshState} />
              )}
              {hasProject && retainerPaid && (
                <div className="mb-6 rounded-xl border border-[#10b981]/25 bg-[#ecfdf5] p-5 flex items-center gap-3" data-testid="retainer-active-badge">
                  <Shield className="h-5 w-5 text-[#10b981] shrink-0" />
                  <span className="text-[14px] text-[#065f46] font-medium">Retainer Active — Ongoing support and quarterly reviews included</span>
                </div>
              )}
              <ProjectTrackerV2 tenantId={tenantId} onProjectStatusChange={refreshState} />
            </>
          )}
          </div>
        </main>
      </div>
    </div>
  );
}
